import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, CheckCircle2, AlertTriangle, XCircle, ArrowRight, RefreshCw, Mail, X } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  recommended: { label: "Recommended", color: "emerald", icon: CheckCircle2 },
  caution: { label: "Caution", color: "amber", icon: AlertTriangle },
  avoid: { label: "Avoid", color: "red", icon: XCircle },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [recs, setRecs] = useState(null);
  const [summary, setSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [verifyLink, setVerifyLink] = useState("");
  const [verifyEmailSent, setVerifyEmailSent] = useState(false);
  const [verifyDismissed, setVerifyDismissed] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const requestVerify = async () => {
    try {
      const r = await api.post("/auth/verify-email/request");
      if (r.data.already_verified) {
        toast.success("Email already verified");
        return;
      }
      if (r.data.email_sent) {
        setVerifyEmailSent(true);
        toast.success("Verification email sent");
      }
      if (r.data.token) {
        setVerifyLink(`${window.location.origin}/verify-email?token=${r.data.token}`);
      }
    } catch {
      toast.error("Could not send verification email");
    }
  };

  const copyVerify = async () => {
    await navigator.clipboard.writeText(verifyLink);
    toast.success("Link copied");
  };

  const load = async () => {
    try {
      const r = await api.get("/recommendations", { params: { limit: 18 } });
      setRecs(r.data);
    } catch (e) {
      toast.error("Could not load recommendations");
    }
    loadSummary();
  };

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const r = await api.get("/recommendations/summary");
      setSummary(r.data.summary || "");
    } catch (e) {
      // ignore
    } finally {
      setSummaryLoading(false);
    }
  };

  if (!recs) {
    return (
      <div className="p-6 md:p-10 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 bg-emerald-100 rounded" />
          <div className="h-32 bg-emerald-50 rounded-2xl" />
          <div className="h-64 bg-emerald-50 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="flex items-start justify-between mb-8 fade-up">
        <div>
          <p className="text-sm text-emerald-700 font-semibold tracking-wider uppercase mb-1">Good to see you</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Hi, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-slate-500 mt-2">Here&apos;s what to eat — and what to skip — for your health today.</p>
        </div>
      </div>

      {/* Email verification banner */}
      {!user?.email_verified && !verifyDismissed && (
        <Card className="rounded-2xl border-sky-100 bg-sky-50/60 p-4 mb-6 flex items-center gap-3 fade-up" data-testid="verify-banner">
          <Mail className="w-5 h-5 text-sky-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            {!verifyEmailSent && !verifyLink && (
              <>
                <div className="text-sm font-medium text-slate-900">Verify your email</div>
                <div className="text-xs text-slate-600">Confirm your address to secure your account.</div>
              </>
            )}
            {verifyEmailSent && !verifyLink && (
              <>
                <div className="text-sm font-medium text-slate-900">Verification email sent</div>
                <div className="text-xs text-slate-600">Check your inbox at <span className="font-medium">{user?.email}</span>. Link expires in 24 hours.</div>
              </>
            )}
            {verifyLink && (
              <>
                <div className="text-sm font-medium text-slate-900">
                  {verifyEmailSent ? "Emailed — or use this backup link" : "Verification link"}
                </div>
                <div className="text-xs text-slate-700 truncate font-mono mt-0.5" data-testid="verify-link">{verifyLink}</div>
              </>
            )}
          </div>
          {!verifyEmailSent && !verifyLink && (
            <Button size="sm" variant="outline" className="rounded-full border-sky-200" onClick={requestVerify} data-testid="verify-request-btn">
              Send email
            </Button>
          )}
          {verifyLink && (
            <>
              <Button size="sm" variant="outline" className="rounded-full" onClick={copyVerify}>Copy</Button>
              <Link to={verifyLink.replace(window.location.origin, "")}>
                <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700">Open</Button>
              </Link>
            </>
          )}
          <Button size="icon" variant="ghost" onClick={() => setVerifyDismissed(true)}>
            <X className="w-4 h-4" />
          </Button>
        </Card>
      )}

      {/* AI summary */}
      <Card className="rounded-3xl border-emerald-100 p-6 mb-8 fade-up delay-1 bg-gradient-to-br from-white to-emerald-50/50">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-2xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-emerald-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="font-display font-semibold text-slate-900">Your personalized guidance</div>
              <Button variant="ghost" size="sm" onClick={loadSummary} disabled={summaryLoading} data-testid="refresh-summary">
                <RefreshCw className={`w-4 h-4 ${summaryLoading ? "animate-spin" : ""}`} />
              </Button>
            </div>
            <p className="text-slate-700 leading-relaxed text-sm md:text-base" data-testid="ai-summary">
              {summaryLoading ? "Generating tailored advice…" : (summary || "Setting up your personalized guidance…")}
            </p>
          </div>
        </div>
      </Card>

      {/* Counts */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        {["recommended", "caution", "avoid"].map((k, i) => {
          const meta = STATUS[k];
          const Icon = meta.icon;
          return (
            <Card key={k} className={`rounded-2xl border-${meta.color}-100 p-5 bg-white fade-up delay-${i + 2}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl bg-${meta.color}-50 flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 text-${meta.color}-600`} />
                </div>
                <div>
                  <div className={`text-xs font-semibold uppercase tracking-wider text-${meta.color}-700`}>{meta.label}</div>
                  <div className="font-display text-2xl font-bold text-slate-900" data-testid={`count-${k}`}>{recs[k].length}</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="recommended" className="fade-up delay-3">
        <TabsList className="bg-emerald-50/70 rounded-full p-1.5 mb-6 h-auto">
          {["recommended", "caution", "avoid"].map((k) => (
            <TabsTrigger
              key={k}
              value={k}
              data-testid={`tab-${k}`}
              className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow text-sm capitalize px-5 py-2"
            >
              {STATUS[k].label} ({recs[k].length})
            </TabsTrigger>
          ))}
        </TabsList>

        {["recommended", "caution", "avoid"].map((bucket) => (
          <TabsContent key={bucket} value={bucket}>
            {recs[bucket].length === 0 ? (
              <Card className="rounded-2xl border-dashed border-emerald-200 p-12 text-center text-slate-400">
                No items in this category.
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {recs[bucket].map((r) => (
                  <FoodCard key={r.food.id} rec={r} bucket={bucket} />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <div className="mt-8 text-center">
        <Link to="/foods">
          <Button variant="outline" className="rounded-full border-emerald-200 hover:bg-emerald-50" data-testid="browse-foods-cta">
            Browse the full food library <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

function FoodCard({ rec, bucket }) {
  const meta = STATUS[bucket];
  const color = meta.color;
  const Icon = meta.icon;
  return (
    <Link to={`/foods/${rec.food.id}`} data-testid={`food-card-${rec.food.id}`}>
      <Card className={`rounded-2xl border-${color}-100 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all bg-white h-full`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="font-display font-semibold text-slate-900 leading-tight">{rec.food.name}</div>
          <Badge className={`bg-${color}-50 text-${color}-700 border-${color}-100 rounded-full flex-shrink-0`}>
            <Icon className="w-3 h-3 mr-1" /> {meta.label}
          </Badge>
        </div>
        <div className="text-xs text-slate-400 mb-3">{rec.food.category}</div>
        {rec.reasons[0] && (
          <p className="text-sm text-slate-600 line-clamp-2">{rec.reasons[0]}</p>
        )}
        <div className="flex gap-3 mt-3 text-xs text-slate-500">
          <span>{rec.food.kcal} kcal</span>
          <span>•</span>
          <span>{rec.food.protein_g}g protein</span>
          <span>•</span>
          <span>{rec.food.fiber_g}g fiber</span>
        </div>
      </Card>
    </Link>
  );
}
